import React, { createContext, useState, useEffect } from "react";
import {
  getItem as storageGetItem,
  setItem as storageSetItem,
} from "../utils/storage";
import { setLogoutHandler } from "../authBridge.js";
import { logoutUser } from "../services/users";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, localSetUser] = useState(null);
  const [error, setError] = useState(null);

  const [authLoaded, setAuthLoaded] = useState(false);

  // Load authentication status and user info from localStorage on initialization
  useEffect(() => {
    setLogoutHandler(() => {
      console.log("!!! LOGOUT TRIGGERED");
      setIsAuthenticated(false);
      setUser(null);
      logoutUser();
      console.log("!!! LOGOUT COMPLETE");
    });

    const loadAuthData = async () => {
      try {
        const savedIsAuthenticated = await storageGetItem("userAuthenticated");
        const savedUser = await storageGetItem("user");

        if (savedIsAuthenticated !== null) {
          setIsAuthenticated(JSON.parse(savedIsAuthenticated));
        }
        if (savedUser !== null) {
          setUser(JSON.parse(savedUser));
        }
      } catch (error) {
        console.error("Error loading authentication data:", error);
        setError("Failed to load authentication data.", error);
      }
      setAuthLoaded(true);
    };

    loadAuthData();
  }, []);

  // Save authentication data to localStorage whenever it changes
  useEffect(() => {
    if (!authLoaded) return;
    const saveAuthData = async () => {
      try {
        await storageSetItem(
          "userAuthenticated",
          JSON.stringify(isAuthenticated),
        );
        await storageSetItem("user", JSON.stringify(user));
      } catch (error) {
        console.error("Error saving authentication data:", error);
        setError("Failed to save authentication data.", error);
      }
    };

    saveAuthData();
  }, [authLoaded, isAuthenticated, user]);

  const setUser = async (user, updateProfile = false) => {
    localSetUser(user);
    if (updateProfile) {
      return;
    }

    if (user) {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }
  };

  const isDev = () => {
    if (!user) return false;

    return (
      (user && user.is_dev) ||
      user.email.match(/@agentwire.ai$/) ||
      user.email === "kwoolery@gmail.com" ||
      user.email === "kwoolery+testing@gmail.com" ||
      user.email === "dhdorfman@yahoo.com"
    );
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        getIsAuthenticated: () => isAuthenticated,
        user,
        setUser,
        getUser: () => user,
        isDev,
        authLoaded,
        activeUser: user, // For backward compatibility
        setActiveUser: setUser, // For backward compatibility
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error(
      "useAuthContext must be used within an AuthProvider",
    );
  }
  return context;
};

export default AuthContext;
